import React from "react";
import {
  Activity,
  Eye,
  Users,
  MapPin,
  Sparkles,
  ShieldCheck,
} from "lucide-react";
import { ProgressRing } from "../ProgressRing";
import { market } from "../../data/offerData";

const Section = ({ title, icon: Icon, action, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white cf-shadow p-5">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={14} className="text-slate-500" />}
        <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">{title}</p>
      </div>
      {action && <button className="text-[11.5px] font-semibold text-indigo-700 hover:underline">{action}</button>}
    </div>
    {children}
  </div>
);

const heatColor = (v) => {
  // 0..24 scale
  if (v >= 18) return "bg-indigo-600";
  if (v >= 12) return "bg-indigo-500";
  if (v >= 8) return "bg-indigo-400";
  if (v >= 5) return "bg-indigo-300";
  if (v >= 3) return "bg-indigo-200";
  return "bg-slate-100";
};

export const MarketInsightsPanel = () => {
  return (
    <aside
      className="hidden xl:flex fixed right-0 top-0 w-[360px] h-screen flex-col border-l cf-divider-soft z-30 cf-scroll overflow-y-auto" style={{ background: "var(--cf-app-bg)" }}
      data-testid="market-insights-panel"
    >
      <div className="px-6 pt-6 pb-2 sticky top-0 z-10">
        <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Market intelligence</p>
        <h3 className="font-display text-[20px] font-bold text-slate-900 mt-1">SW11 · live</h3>
      </div>

      <div className="px-6 pb-8 space-y-4">
        {/* Demand score */}
        <div className="rounded-2xl border border-slate-200 bg-white cf-shadow p-5" data-testid="demand-score-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Market demand score</p>
              <p className="font-display text-[28px] font-bold text-slate-900 mt-1 leading-none">{market.demandScore}<span className="text-[14px] text-slate-400">/100</span></p>
              <p className="text-[11.5px] text-indigo-700 font-semibold mt-1">High demand · +12% MoM</p>
            </div>
            <ProgressRing value={market.demandScore} size={68} stroke={6} color="#6366F1" label={`${market.demandScore}`} />
          </div>
        </div>

        {/* Conversion */}
        <Section title="Viewing → offer conversion" icon={Eye}>
          <div className="flex items-baseline gap-2">
            <p className="font-display text-[24px] font-bold text-slate-900">{market.viewingsToOffer}%</p>
            <span className="text-[11.5px] text-indigo-700 font-semibold">+3.2pp vs network</span>
          </div>
          <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-indigo-500" style={{ width: `${market.viewingsToOffer * 5}%` }} />
          </div>
          <p className="text-[11px] text-slate-500 mt-2">27 viewings yielded 4 formal offers</p>
        </Section>

        {/* Heatmap */}
        <Section title="Competing buyer heatmap" icon={Users}>
          <p className="text-[11.5px] text-slate-500 mb-3">Buyer activity by day · last 4 weeks</p>
          <div className="grid grid-rows-4 gap-1">
            {market.heat.map((row, i) => (
              <div key={i} className="grid grid-cols-7 gap-1">
                {row.map((v, j) => (
                  <div
                    key={j}
                    className={`h-5 rounded-sm ${heatColor(v)} transition-colors`}
                    title={`${v} active buyers`}
                  />
                ))}
              </div>
            ))}
          </div>
          <div className="mt-2 grid grid-cols-7 gap-1 text-[9px] uppercase text-slate-400 tracking-[0.14em] text-center">
            {["M","T","W","T","F","S","S"].map((d, i) => <span key={i}>{d}</span>)}
          </div>
          <div className="mt-3 flex items-center justify-between">
            <p className="text-[11.5px] text-slate-500">9 active buyers in £580k–£650k bracket</p>
            <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-indigo-700">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 cf-pulse" /> Live
            </span>
          </div>
        </Section>

        {/* AI pricing confidence */}
        <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-5 cf-shadow" data-testid="pricing-confidence-card">
          <div className="flex items-center gap-2 text-indigo-300">
            <Sparkles size={14} />
            <p className="text-[10.5px] uppercase tracking-[0.22em] font-semibold">AI pricing confidence</p>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <p className="font-display text-[34px] font-bold leading-none">{market.pricingConfidence}%</p>
            <span className="text-[11.5px] text-indigo-300 font-semibold">High</span>
          </div>
          <p className="text-[12px] text-slate-300 mt-2">Asking price aligned with current £/sqft band — modest headroom on best offer.</p>
          <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-indigo-400" style={{ width: `${market.pricingConfidence}%` }} />
          </div>
        </div>

        {/* Comparables */}
        <Section title="Similar properties" icon={MapPin} action="Compare">
          <ul className="space-y-3">
            {market.comparables.map((c, i) => (
              <li key={i} className="flex items-center gap-3" data-testid={`comp-${i}`}>
                <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center font-display font-bold text-[11px]">
                  {c.price.replace("£", "").replace("k", "")}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12.5px] font-medium text-slate-900 truncate">{c.addr}</p>
                  <p className="text-[10.5px] text-slate-500">{c.days}d · {c.status}</p>
                </div>
                <span className={`text-[10.5px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${
                  c.status === "Exchanged" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : c.status === "STC" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50 text-slate-700 border-slate-200"
                }`}>{c.status}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Local intelligence */}
        <Section title="Local market intelligence" icon={ShieldCheck}>
          <ul className="space-y-2.5">
            {market.intelligence.map((line, i) => (
              <li key={i} className="flex items-start gap-2" data-testid={`local-${i}`}>
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
                <p className="text-[12.5px] text-slate-700 leading-relaxed">{line}</p>
              </li>
            ))}
          </ul>
        </Section>
      </div>
    </aside>
  );
};
